import { useEffect, useMemo } from 'react';

import { useState } from 'react';
import { IUploadResults, QUOTE_TYPE } from 'types/bulkUpload';
import { useHistory } from 'react-router-dom';
import { templateWithErrorsInfo, templateUploadedInfo } from '../DonwloadUploadQuotes/content/donwloadUploadInfo';
import { IUploadInfo } from '../UploadCatalog/content/uploadCatalogInfo';
import { uploadTemplateInfoWithErrors, downloadUploadInfo } from '../DonwloadUploadQuotes/content/donwloadUploadInfo';
import UploadForm from '../components/UploadForm/UploadForm';
import { SummarySubmitSection } from '../UploadCatalog/components/SummarySubmitSection/SummarySubmitSection';
import QuoteTypeSelector from '../DonwloadUploadQuotes/components/DownloadQuotesStep0/components/QuoteTypeSelector/QuoteTypeSelector';
import BulkUploadHeader from '../components/BulkUploadHeader/BulkUploadHeader';
import { UPLOAD_QUOTES_STEPS } from './constants';
import { uploadQuotes } from 'services/bulkUpload/uploadQuotes.service';
import { displayErrorNotification } from 'utils/displayNudosStandardNotifications';
import './UploadQuotes.scss';

const UploadQuotesModule = () => {
  const { push } = useHistory();
  const [currentStep, setCurrentStep] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);
  const [errorTemplateURL, setErrorTemplateURL] = useState<string | undefined>();
  const [uploadResults, setUploadResults] = useState<IUploadResults | undefined>();
  const [selectedQuoteType, setSelectedQuoteType] = useState<QUOTE_TYPE>(QUOTE_TYPE.REGULAR);
  const [goBackText, setGoBackText] = useState('');

  const handleSubmitUploadForm = async (file: File) => {
    try {
      if (!selectedQuoteType) throw new SyntaxError('No se ha seleccionado un tipo de cotización');
      const uploadQuotesResponse = await uploadQuotes(file, selectedQuoteType);
      setErrors(uploadQuotesResponse?.errors || []);
      setUploadResults(uploadQuotesResponse);
      setErrorTemplateURL(uploadQuotesResponse?.errorFileUrl);
      nextStep();
    } catch (error) {
      const errorMessage =
        error instanceof SyntaxError ? error.message : 'Error al subir las cotizaciones, por favor, inténtelo de nuevo';
      displayErrorNotification({
        customJSXMessage: <>{errorMessage}</>,
        duration: 5000
      });
    }
  };

  const handleBack = () => {
    if (currentStep === 0) {
      push('/bulk-upload/type-of-charge');
    } else {
      setCurrentStep(curr => curr - 1);
    }
  };

  const nextStep = () => setCurrentStep(curr => curr + 1);

  const summaryUploadInfo = useMemo<IUploadInfo>(() => {
    const updatedRecords = uploadResults?.updatedRecords || 0;
    const totalRecords = uploadResults?.totalRecords || 0;
    if (!!errors?.length) {
      return templateWithErrorsInfo(updatedRecords, totalRecords);
    }
    return templateUploadedInfo(updatedRecords, totalRecords);
  }, [errors, uploadResults]);

  const uploadInfo = useMemo<IUploadInfo>(() => {
    if (!!errors?.length) {
      return uploadTemplateInfoWithErrors(errorTemplateURL || '');
    }
    return downloadUploadInfo;
  }, [errors, errorTemplateURL]);

  const handleReturnToUpload = () => {
    setCurrentStep(curr => curr - 1);
  };

  const renderComponentInStep = (step: number) => {
    switch (step) {
      case 0:
        return (
          <UploadForm
            key={`step-quotes-${step}`}
            onSubmit={handleSubmitUploadForm}
            backLink="/bulk-upload/type-of-charge"
            uploadInfo={uploadInfo}
            customClassName="uploadQuotesForm"
            customContentBeforeOptions={
              <QuoteTypeSelector
                selectedQuoteType={selectedQuoteType || QUOTE_TYPE.REGULAR}
                onSelectQuoteType={setSelectedQuoteType}
              />
            }
          />
        );
      case 1:
        return (
          <UploadForm
            key={`step-quotes-${step}`}
            onSubmit={handleSubmitUploadForm}
            backLink="/bulk-upload/type-of-charge"
            hideAlert
            hideFileInput
            customSubmitSection={
              <SummarySubmitSection
                areErrors={!!errors?.length}
                errorTemplateURL={errorTemplateURL}
                handleReturnToUpload={handleReturnToUpload}
                typeOfSummary="quotes"
              />
            }
            uploadInfo={summaryUploadInfo}
            errors={errors}
            customClassName="uploadQuotesForm"
          />
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    switch (currentStep) {
      case 0:
        setGoBackText('Volver a Cargue');
        break;
      case 1:
        setGoBackText(`Volver a ${UPLOAD_QUOTES_STEPS[0]}`);
        break;
      default:
        setGoBackText('');
    }
  }, [currentStep]);

  return (
    <main className="upload-quotes-container">
      <BulkUploadHeader
        steps={UPLOAD_QUOTES_STEPS}
        currentStep={currentStep}
        handleBack={handleBack}
        goBackText={goBackText}
      />
      {renderComponentInStep(currentStep)}
    </main>
  );
};
export default UploadQuotesModule;
